import React from "react";
import SectionCrudPage from "./SectionCrudPage";
import {
  CrmDateTimeInput,
  CrmDateInput,
  CrmFormGrid,
  CrmNumberInput,
  CrmSelectInput,
  CrmTextInput,
} from "../../components/crm/CrudFormControls";

const asText = (value) => String(value ?? "").trim();
const asNullableText = (value) => {
  const next = asText(value);
  return next === "" ? null : next;
};
const asNullable = (value) => (value === "" || value === undefined ? null : value);
const asNumber = (value) => {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};
const asNullableNumber = (value) => {
  if (value === "" || value === undefined || value === null) return null;
  return asNumber(value);
};
const asDateTime = (value) => {
  const next = asText(value);
  if (next === "") return "";
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(next)) return `${next.replace("T", " ")}:00`;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(next)) return next.replace("T", " ");
  return next;
};
const asNullableDateTime = (value) => {
  const next = asText(value);
  if (next === "") return null;
  return asDateTime(next);
};

const selectSource = (name, endpoint, labelFields, valueField = "id") => ({
  name,
  optionSource: { endpoint, valueField, labelFields },
});

function AssetTypesForm({ formData, setFormData, errors, clearFieldError }) {
  return (
    <CrmFormGrid>
      <CrmTextInput
        name="code"
        label="Code"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="name"
        label="Name"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
    </CrmFormGrid>
  );
}

function AssetLocationsForm({ formData, setFormData, errors, clearFieldError, selectOptions }) {
  return (
    <CrmFormGrid>
      <CrmSelectInput
        name="zone_id"
        label="Zone"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.zone_id || []}
        nullable
      />
      <CrmTextInput
        name="name"
        label="Name"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
    </CrmFormGrid>
  );
}

function AssetsForm({ formData, setFormData, errors, clearFieldError, selectOptions }) {
  return (
    <CrmFormGrid>
      <CrmSelectInput
        name="status"
        label="Status"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={[
          { value: "active", label: "Active" },
          { value: "maintenance", label: "Maintenance" },
          { value: "inactive", label: "Inactive" },
          { value: "retired", label: "Retired" },
        ]}
      />
      <CrmSelectInput
        name="asset_type_id"
        label="Asset Type"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.asset_type_id || []}
      />
      <CrmSelectInput
        name="location_id"
        label="Asset Location"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.location_id || []}
        nullable
      />
      <CrmTextInput
        name="name"
        label="Name"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="brand"
        label="Brand"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="model"
        label="Model"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="serial_number"
        label="Serial Number"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmDateInput
        name="purchase_date"
        label="Purchase Date (YYYY-MM-DD)"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmNumberInput
        name="purchase_cost"
        label="Purchase Cost"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmDateInput
        name="warranty_until"
        label="Warranty Until (YYYY-MM-DD)"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="notes"
        label="Notes"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
    </CrmFormGrid>
  );
}

function MaintenancePlansForm({ formData, setFormData, errors, clearFieldError, selectOptions }) {
  return (
    <CrmFormGrid>
      <CrmSelectInput
        name="asset_id"
        label="Asset"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.asset_id || []}
      />
      <CrmSelectInput
        name="plan_type"
        label="Plan Type"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={[
          { value: "preventive", label: "Preventive" },
          { value: "inspection", label: "Inspection" },
        ]}
      />
      <CrmNumberInput
        name="interval_days"
        label="Interval Days"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmDateInput
        name="next_due_date"
        label="Next Due Date (YYYY-MM-DD)"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="notes"
        label="Notes"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
    </CrmFormGrid>
  );
}

function MaintenanceLogsForm({ formData, setFormData, errors, clearFieldError, selectOptions }) {
  return (
    <CrmFormGrid>
      <CrmSelectInput
        name="asset_id"
        label="Asset"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.asset_id || []}
      />
      <CrmSelectInput
        name="log_type"
        label="Log Type"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={[
          { value: "preventive", label: "Preventive" },
          { value: "corrective", label: "Corrective" },
          { value: "inspection", label: "Inspection" },
        ]}
      />
      <CrmDateTimeInput
        name="occurred_at"
        label="Occurred At (YYYY-MM-DD HH:MM:SS)"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="description"
        label="Description"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmNumberInput
        name="cost"
        label="Cost"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmSelectInput
        name="supplier_id"
        label="Supplier"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.supplier_id || []}
        nullable
      />
      <CrmSelectInput
        name="created_by_user_id"
        label="Created By User"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.created_by_user_id || []}
        nullable
      />
    </CrmFormGrid>
  );
}

function AssetIncidentsForm({ formData, setFormData, errors, clearFieldError, selectOptions }) {
  return (
    <CrmFormGrid>
      <CrmSelectInput
        name="asset_id"
        label="Asset"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.asset_id || []}
      />
      <CrmSelectInput
        name="status"
        label="Status"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={[
          { value: "open", label: "Open" },
          { value: "in_progress", label: "In Progress" },
          { value: "resolved", label: "Resolved" },
          { value: "closed", label: "Closed" },
        ]}
      />
      <CrmSelectInput
        name="severity"
        label="Severity"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={[
          { value: "low", label: "Low" },
          { value: "medium", label: "Medium" },
          { value: "high", label: "High" },
        ]}
      />
      <CrmDateTimeInput
        name="reported_at"
        label="Reported At (YYYY-MM-DD HH:MM:SS)"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="description"
        label="Description"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmDateTimeInput
        name="resolved_at"
        label="Resolved At (YYYY-MM-DD HH:MM:SS)"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="resolution_notes"
        label="Resolution Notes"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmSelectInput
        name="reported_by_user_id"
        label="Reported By User"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.reported_by_user_id || []}
        nullable
      />
    </CrmFormGrid>
  );
}

const assetsResources = [
  {
    key: "asset-types",
    label: "Asset Types",
    endpoint: "/asset-types",
    relatedTabs: [
      {
        key: "assets",
        label: "Assets",
        endpoint: "/assets",
        foreignKey: "asset_type_id",
      },
    ],
    renderForm: AssetTypesForm,
    toPayload: (formData) => ({
      code: asText(formData.code),
      name: asText(formData.name),
    }),
  },
  {
    key: "asset-locations",
    label: "Asset Locations",
    endpoint: "/asset-locations",
    tableFields: [
      {
        name: "zone_id",
        label: "Zone",
        valueGetter: (row) => row?.zone_name || row?.zone?.name || row?.zone_id,
      },
      { name: "name", label: "Name" },
      { name: "created_at", label: "Created At" },
      { name: "updated_at", label: "Updated At" },
    ],
    relatedTabs: [
      {
        key: "assets",
        label: "Assets",
        endpoint: "/assets",
        foreignKey: "location_id",
      },
    ],
    selectSources: [
      selectSource("zone_id", "/zones", ["name", "code", "uuid"]),
    ],
    renderForm: AssetLocationsForm,
    toPayload: (formData) => ({
      zone_id: asNullable(formData.zone_id),
      name: asText(formData.name),
    }),
  },
  {
    key: "assets",
    label: "Assets",
    endpoint: "/assets",
    tableFields: [
      { name: "status", label: "Status" },
      {
        name: "asset_type_id",
        label: "Asset Type",
        valueGetter: (row) => row?.asset_type_name || row?.asset_type?.name || row?.asset_type_id,
      },
      {
        name: "location_id",
        label: "Location",
        valueGetter: (row) => row?.location_name || row?.location?.name || row?.location_id,
      },
      { name: "name", label: "Name" },
      { name: "brand", label: "Brand" },
      { name: "model", label: "Model" },
      { name: "serial_number", label: "Serial Number" },
      { name: "purchase_date", label: "Purchase Date" },
      { name: "purchase_cost", label: "Purchase Cost" },
      { name: "warranty_until", label: "Warranty Until" },
      { name: "created_at", label: "Created At" },
      { name: "updated_at", label: "Updated At" },
    ],
    relatedTabs: [
      {
        key: "asset-maintenance-plans",
        label: "Maintenance Plans",
        endpoint: "/asset-maintenance-plans",
        foreignKey: "asset_id",
      },
      {
        key: "asset-maintenance-logs",
        label: "Maintenance Logs",
        endpoint: "/asset-maintenance-logs",
        foreignKey: "asset_id",
      },
      {
        key: "asset-incidents",
        label: "Incidents",
        endpoint: "/asset-incidents",
        foreignKey: "asset_id",
      },
    ],
    selectSources: [
      selectSource("asset_type_id", "/asset-types", ["name", "code", "uuid"]),
      selectSource("location_id", "/asset-locations", ["name", "uuid"]),
    ],
    renderForm: AssetsForm,
    toPayload: (formData) => ({
      status: asText(formData.status),
      asset_type_id: asNullable(formData.asset_type_id),
      location_id: asNullable(formData.location_id),
      name: asText(formData.name),
      brand: asNullableText(formData.brand),
      model: asNullableText(formData.model),
      serial_number: asNullableText(formData.serial_number),
      purchase_date: asNullableText(formData.purchase_date),
      purchase_cost: asNullableNumber(formData.purchase_cost),
      warranty_until: asNullableText(formData.warranty_until),
      notes: asNullableText(formData.notes),
    }),
  },
  {
    key: "asset-maintenance-plans",
    label: "Maintenance Plans",
    endpoint: "/asset-maintenance-plans",
    tableFields: [
      {
        name: "asset_id",
        label: "Asset",
        valueGetter: (row) => row?.asset_name || row?.asset?.name || row?.asset_id,
      },
      { name: "plan_type", label: "Plan Type" },
      { name: "interval_days", label: "Interval Days" },
      { name: "next_due_date", label: "Next Due Date" },
      { name: "notes", label: "Notes" },
      { name: "created_at", label: "Created At" },
      { name: "updated_at", label: "Updated At" },
    ],
    selectSources: [
      selectSource("asset_id", "/assets", ["name", "serial_number", "uuid"]),
    ],
    renderForm: MaintenancePlansForm,
    toPayload: (formData) => ({
      asset_id: asNullable(formData.asset_id),
      plan_type: asText(formData.plan_type),
      interval_days: asNumber(formData.interval_days),
      next_due_date: asNullableText(formData.next_due_date),
      notes: asNullableText(formData.notes),
    }),
  },
  {
    key: "asset-maintenance-logs",
    label: "Maintenance Logs",
    endpoint: "/asset-maintenance-logs",
    tableFields: [
      {
        name: "asset_id",
        label: "Asset",
        valueGetter: (row) => row?.asset_name || row?.asset?.name || row?.asset_id,
      },
      { name: "log_type", label: "Log Type" },
      { name: "occurred_at", label: "Occurred At" },
      { name: "description", label: "Description" },
      { name: "cost", label: "Cost" },
      {
        name: "supplier_id",
        label: "Supplier",
        valueGetter: (row) => row?.supplier_name || row?.supplier?.name || row?.supplier_id,
      },
      {
        name: "created_by_user_id",
        label: "Created By",
        valueGetter: (row) => row?.created_by_user_name || row?.created_by_user?.name || row?.created_by_user?.email || row?.created_by_user_id,
      },
      { name: "created_at", label: "Created At" },
      { name: "updated_at", label: "Updated At" },
    ],
    selectSources: [
      selectSource("asset_id", "/assets", ["name", "serial_number", "uuid"]),
      selectSource("supplier_id", "/suppliers", ["name", "uuid"]),
      selectSource("created_by_user_id", "/users", ["name", "email"]),
    ],
    renderForm: MaintenanceLogsForm,
    toPayload: (formData) => ({
      asset_id: asNullable(formData.asset_id),
      log_type: asText(formData.log_type),
      occurred_at: asDateTime(formData.occurred_at),
      description: asText(formData.description),
      cost: asNullableNumber(formData.cost),
      supplier_id: asNullable(formData.supplier_id),
      created_by_user_id: asNullable(formData.created_by_user_id),
    }),
  },
  {
    key: "asset-incidents",
    label: "Asset Incidents",
    endpoint: "/asset-incidents",
    tableFields: [
      {
        name: "asset_id",
        label: "Asset",
        valueGetter: (row) => row?.asset_name || row?.asset?.name || row?.asset_id,
      },
      { name: "status", label: "Status" },
      { name: "severity", label: "Severity" },
      { name: "reported_at", label: "Reported At" },
      { name: "description", label: "Description" },
      { name: "resolved_at", label: "Resolved At" },
      { name: "resolution_notes", label: "Resolution Notes" },
      {
        name: "reported_by_user_id",
        label: "Reported By",
        valueGetter: (row) => row?.reported_by_user_name || row?.reported_by_user?.name || row?.reported_by_user?.email || row?.reported_by_user_id,
      },
      { name: "created_at", label: "Created At" },
      { name: "updated_at", label: "Updated At" },
    ],
    selectSources: [
      selectSource("asset_id", "/assets", ["name", "serial_number", "uuid"]),
      selectSource("reported_by_user_id", "/users", ["name", "email"]),
    ],
    renderForm: AssetIncidentsForm,
    toPayload: (formData) => ({
      asset_id: asNullable(formData.asset_id),
      status: asText(formData.status),
      severity: asText(formData.severity),
      reported_at: asDateTime(formData.reported_at),
      description: asText(formData.description),
      resolved_at: asNullableDateTime(formData.resolved_at),
      resolution_notes: asNullableText(formData.resolution_notes),
      reported_by_user_id: asNullable(formData.reported_by_user_id),
    }),
  },
];

export default function AssetsPage() {
  return (
    <SectionCrudPage
      title="Assets"
      subtitle="Manage equipment, locations, maintenance plans/logs and incidents."
      resources={assetsResources}
    />
  );
}
